import { getTradeTaxRate, hstFromInclusive } from "../constants/tax";

export function agentTradeNumberSet(trades) {
  const set = new Set();
  for (const trade of trades || []) {
    const n = String(
      trade?.tradeNumber ?? trade?.keyInfo?.tradeNumber ?? "",
    ).trim();
    if (n) set.add(n);
  }
  return set;
}

function agentIdentitySet(appUser) {
  return new Set(
    [appUser?.employeeNo, appUser?.agentId]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean),
  );
}

export function agentRowMatchesAppUser(row, appUser) {
  if (!row || !appUser) return false;
  const ids = agentIdentitySet(appUser);
  const rowId = String(row.agentId ?? "").trim();
  if (rowId && ids.has(rowId)) return true;
  const name = String(appUser.displayName || "").trim().toLowerCase();
  const rowName = String(row.agentName || "").trim().toLowerCase();
  if (!name || !rowName) return false;
  return (
    rowName === name ||
    rowName.includes(name) ||
    name.includes(rowName)
  );
}

function descriptionNamesAgent(desc, appUser) {
  const text = String(desc || "");
  const name = String(appUser?.displayName || "").trim().toLowerCase();
  if (name && text.toLowerCase().includes(name)) return true;
  const emp = String(appUser?.employeeNo ?? "").trim();
  if (emp && new RegExp(`(^|\\D)${emp}(\\D|$)`).test(text)) return true;
  return false;
}

function tradesByNumberMap(trades) {
  const map = new Map();
  for (const trade of trades || []) {
    const n = String(
      trade?.tradeNumber ?? trade?.keyInfo?.tradeNumber ?? "",
    ).trim();
    if (n) map.set(n, trade);
  }
  return map;
}

/**
 * Attribute a ledger line to the logged-in agent.
 * On multi-agent trades, shared lines (e.g. full-trade HST Collected) that only
 * mention Trade # are NOT attributed — use buildAgentSpecificHstEntries instead.
 */
export function ledgerBelongsToAgent(entry, { tradeNumbers, appUser, trades }) {
  if (!entry) return false;
  const desc = String(entry.description || "");

  if (descriptionNamesAgent(desc, appUser)) return true;

  const tn = desc.match(/Trade\s*#:?\s*(\d+(?:-[ABC])?)/i);
  if (!tn || !tradeNumbers?.has(tn[1])) return false;

  const byNumber = tradesByNumberMap(trades);
  const trade = byNumber.get(tn[1]);
  if (!trade) return true;

  const agentsOnTrade = Array.isArray(trade.agentCommissionList)
    ? trade.agentCommissionList
    : [];
  if (agentsOnTrade.length <= 1) return true;

  const myRows = agentsOnTrade.filter((row) =>
    agentRowMatchesAppUser(row, appUser),
  );
  // Solo match among listed agents with no other co-agents → allow trade lines.
  if (myRows.length > 0 && myRows.length === agentsOnTrade.length) return true;

  // Multi-agent shared ledger line without this agent's name → exclude.
  return false;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function tradeDateInRange(trade, fromDate, toDate) {
  const raw =
    trade?.keyInfo?.finalizedDate ||
    trade?.finalizedDate ||
    trade?.keyInfo?.closeDate ||
    trade?.keyInfo?.entryDate ||
    "";
  if (!raw || !fromDate || !toDate) return true;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return true;
  const start = new Date(fromDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(toDate);
  end.setHours(23, 59, 59, 999);
  return d >= start && d <= end;
}

function agentCommissionTax(row, taxRate) {
  const amount = parseFloat(row?.amount) || 0;
  const overridden =
    row?.taxOverridden === true || row?.taxOverridden === "true";
  if (overridden) return round2(parseFloat(row?.tax) || 0);
  return round2(amount * taxRate);
}

function agentFeeTax(row, taxRate) {
  const fees = parseFloat(row?.feesDeducted) || 0;
  return round2(fees * taxRate);
}

/**
 * Synthetic HST ledger rows for the agent's own commission/fees on multi-agent trades.
 * Solo-agent trades keep real ledger lines via ledgerBelongsToAgent.
 */
export function buildAgentSpecificHstEntries(
  trades,
  appUser,
  { fromDate, toDate } = {},
) {
  const entries = [];
  const who = String(appUser?.displayName || "Agent").trim() || "Agent";

  for (const trade of trades || []) {
    const agentsOnTrade = Array.isArray(trade.agentCommissionList)
      ? trade.agentCommissionList
      : [];
    if (agentsOnTrade.length <= 1) continue;

    const myRows = agentsOnTrade.filter((row) =>
      agentRowMatchesAppUser(row, appUser),
    );
    if (!myRows.length) continue;
    if (!tradeDateInRange(trade, fromDate, toDate)) continue;

    const taxRate = getTradeTaxRate(trade);
    let commissionHst = 0;
    let feeHst = 0;
    for (const row of myRows) {
      commissionHst += agentCommissionTax(row, taxRate);
      feeHst += agentFeeTax(row, taxRate);
    }
    commissionHst = round2(commissionHst);
    feeHst = round2(feeHst);
    if (commissionHst <= 0 && feeHst <= 0) continue;

    const tn = String(
      trade.tradeNumber ?? trade.keyInfo?.tradeNumber ?? "",
    ).trim();
    const address = [
      trade.keyInfo?.streetNumber,
      trade.keyInfo?.streetName,
      trade.keyInfo?.unit,
    ]
      .filter(Boolean)
      .join(" ");
    const date =
      trade.keyInfo?.finalizedDate ||
      trade.finalizedDate ||
      trade.keyInfo?.closeDate ||
      trade.keyInfo?.entryDate ||
      null;
    const baseDesc = `Trade #: ${tn}${address ? ` - ${address}` : ""} - ${who}`;

    if (commissionHst > 0) {
      entries.push({
        accountNumber: "23000",
        accountName: "HST Collected",
        debit: 0,
        credit: commissionHst,
        description: `${baseDesc} (agent HST)`,
        date,
      });
    }
    if (feeHst > 0) {
      entries.push({
        accountNumber: "23001",
        accountName: "HST Input Tax Credit",
        debit: feeHst,
        credit: 0,
        description: `${baseDesc} (fee HST)`,
        date,
      });
    }
  }

  return entries;
}

/** Match the EFT payee only — never every payout on a shared trade. */
export function paymentEftBelongsToAgent(eft, appUser) {
  if (!appUser) return true;
  const ids = agentIdentitySet(appUser);
  const eftAgent = String(eft?.agentId ?? "").trim();
  if (eftAgent && ids.has(eftAgent)) return true;
  const name = String(appUser.displayName || "").trim().toLowerCase();
  const eftName = String(eft?.agentName || "").trim().toLowerCase();
  return Boolean(name && eftName && eftName === name);
}

export function resolveLoggedInAgentId(appUser, agents) {
  if (!appUser) return "";
  const byMongo = String(appUser.agentId || "").trim();
  const emp = String(appUser.employeeNo ?? "").trim();
  const name = String(appUser.displayName || "").trim().toLowerCase();
  const match = (agents || []).find((agent) => {
    if (byMongo && String(agent._id) === byMongo) return true;
    if (emp && String(agent.employeeNo ?? "").trim() === emp) return true;
    const full = `${agent.firstName || ""} ${agent.lastName || ""}`
      .trim()
      .toLowerCase();
    return Boolean(name && full && full === name);
  });
  return match ? String(match._id) : byMongo;
}

/**
 * Agent Payment Report HST column = tax portion of HST-inclusive net commission (EFT amount).
 * Always derived from the paid net — do not use trade-sheet HST math here.
 */
export function agentPaymentReportHst(_agentCommission, trade, eftAmount) {
  const inclusiveNet = parseFloat(eftAmount || 0) || 0;
  const taxRate = getTradeTaxRate(trade);
  return Math.round(hstFromInclusive(inclusiveNet, taxRate) * 100) / 100;
}

/** Resolve HST on a report row from net commission when needed. */
export function resolveAgentPaymentRowHst(item) {
  const net = parseFloat(item?.netCommission || 0) || 0;
  const stored = parseFloat(item?.hstAmount);
  if (Number.isFinite(stored) && stored > 0.005) {
    return Math.round(stored * 100) / 100;
  }
  return Math.round(hstFromInclusive(net) * 100) / 100;
}
